import type { CliAuthRequest } from "./constants"
import { clearCliAuthRequest, isLoopbackRedirectUri, readCliAuthRequest } from "./constants"

/**
 * Resolves the pending CLI-auth request for a given location search string.
 *
 * - Valid `redirect_uri` + `state` in the query → use them.
 * - Query params present but invalid → `null` (invalid request; never mask it with a stored one).
 * - No query params at all → fall back to the stored request (the back-from-login case).
 */
export function resolveCliAuthRequest(search: string): CliAuthRequest | null {
	const params = new URLSearchParams(search)
	const redirectUri = params.get("redirect_uri") ?? ""
	const state = params.get("state") ?? ""

	if (redirectUri && state && isLoopbackRedirectUri(redirectUri)) return { redirectUri, state }
	if (params.has("redirect_uri") || params.has("state")) return null
	return readCliAuthRequest()
}

/**
 * Human-readable callback target shown on the consent screen, e.g. "127.0.0.1:53211".
 * `URL.host` includes the port, which is the part that identifies the local tool.
 */
export function describeCallbackHost(redirectUri: string): string {
	try {
		return new URL(redirectUri).host
	} catch {
		return redirectUri
	}
}

/**
 * Hands the active session token back to the loopback callback via a top-level POST form submit.
 *
 * The token travels in the POST body (never the URL), and a top-level navigation to a loopback
 * address is exempt from mixed-content blocking, so there are no CORS or Safari loopback-fetch
 * issues. The pending request is consumed (cleared) before submitting so it can never replay.
 *
 * This is the real token sink, so it re-asserts the loopback guard here — the token must never
 * leave the page toward a non-loopback target, whatever the caller believed about the request.
 */
export function submitCliAuthHandoff(request: CliAuthRequest, accessToken: string): void {
	if (!isLoopbackRedirectUri(request.redirectUri)) return

	clearCliAuthRequest()

	const form = document.createElement("form")
	form.method = "POST"
	form.action = request.redirectUri

	const addField = (name: string, value: string) => {
		const input = document.createElement("input")
		input.type = "hidden"
		input.name = name
		input.value = value
		form.appendChild(input)
	}

	addField("token", accessToken)
	addField("state", request.state)

	document.body.appendChild(form)
	form.submit()
}
